import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  LinearProgress,
  Card,
  CardContent,
  MenuItem,
  TableSortLabel,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Print as PrintIcon,
  PictureAsPdf as PdfIcon,
  Refresh as RefreshIcon,
  FileDownload as ExcelIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { requestService } from '../../services/requestService';
import InvoiceService from '../../services/invoice.service';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { addDarbReportHeader, PDF_TABLE_HEAD_STYLES } from '../../utils/pdfReportHeader';
import api from '../../api/axios';
import { downloadDarbExcelReport } from '../../utils/excelReportExport';

interface ReportRow {
  createdAt: string;
  date: string;
  requestNumber: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear: number | null;
  plateNumber: string;
  phoneNumber: string;
  locationFrom: string;
  locationTo: string;
  serviceType: string;
  issueDescription: string;
  serviceProviderName: string;
  serviceProviderId: number | null;
}

interface ServiceProvider {
  id: number;
  attributes: {
    name: string;
  };
}

type SortField = keyof ReportRow;
type SortOrder = 'asc' | 'desc';

const RequestItemsReport: React.FC = () => {
  const [reportData, setReportData] = useState<ReportRow[]>([]);
  const [filteredData, setFilteredData] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [error, setError] = useState<string>('');
  const [serviceProviders, setServiceProviders] = useState<ServiceProvider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string>('all');
  const [selectedServiceType, setSelectedServiceType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [allTime, setAllTime] = useState(true);

  const BATCH_SIZE = 15;

  const [sortField, setSortField] = useState<SortField>('date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const [periodStart, setPeriodStart] = useState<string>(() => {
    const date = new Date();
    date.setDate(1);
    return date.toISOString().split('T')[0];
  });
  const [periodEnd, setPeriodEnd] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });

  const serviceTypeOptions = useMemo(() => {
    const types = new Set<string>();
    reportData.forEach((row) => {
      if (row.serviceType?.trim()) types.add(row.serviceType.trim());
    });
    return Array.from(types).sort((a, b) => a.localeCompare(b));
  }, [reportData]);

  const isInitialMount = useRef(true);

  useEffect(() => {
    loadServiceProviders();
    loadReport();
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTime]);

  useEffect(() => {
    filterAndSortData();
  }, [reportData, selectedProvider, selectedServiceType, sortField, sortOrder, searchQuery]);

  const loadServiceProviders = async () => {
    try {
      const response = await api.get('/service-providers', {
        params: {
          'pagination[limit]': -1,
          sort: 'name:asc'
        }
      });
      setServiceProviders(response.data.data || []);
    } catch (err) {
      console.error('Error loading service providers:', err);
    }
  };

  const parseLocation = (locationText: string | null | undefined): { from: string; to: string } => {
    if (!locationText) return { from: '', to: '' };
    
    const trimmed = locationText.trim();
    
    // Try to split by tab first (most common for location pairs)
    if (trimmed.includes('\t')) {
      const parts = trimmed.split('\t').map(p => p.trim()).filter(p => p.length > 0);
      if (parts.length >= 2) {
        return { from: parts[0], to: parts[1] };
      }
    }
    
    // Try to split by multiple spaces (common delimiter)
    const spaceParts = trimmed.split(/\s{2,}/).map(p => p.trim()).filter(p => p.length > 0);
    if (spaceParts.length >= 2) {
      return { from: spaceParts[0], to: spaceParts[1] };
    }
    
    // Try common separators
    const separators = [' to ', ' -> ', ' → ', ' - ', ' | '];
    for (const sep of separators) {
      if (trimmed.toLowerCase().includes(sep.toLowerCase())) {
        const parts = trimmed.split(new RegExp(sep, 'i')).map(p => p.trim()).filter(p => p.length > 0);
        if (parts.length >= 2) {
          return { from: parts[0], to: parts[1] };
        }
      }
    }
    
    // If only one location, use it for both
    return { from: trimmed, to: trimmed };
  };

  const filterAndSortData = () => {
    let filtered = [...reportData];

    if (selectedProvider !== 'all') {
      filtered = filtered.filter((row) =>
        row.serviceProviderId?.toString() === selectedProvider
      );
    }

    if (selectedServiceType !== 'all') {
      filtered = filtered.filter((row) => row.serviceType === selectedServiceType);
    }

    // Text search across key fields
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter((row) =>
        [
          row.requestNumber,
          row.phoneNumber,
          row.plateNumber,
          row.vehicleMake,
          row.vehicleModel,
          row.locationFrom,
          row.locationTo,
          row.serviceProviderName,
          row.serviceType,
          row.issueDescription,
        ].some((val) => val?.toLowerCase().includes(q))
      );
    }

    // Sort data
    filtered.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (sortField === 'date') {
        aValue = new Date(a.createdAt).getTime();
        bValue = new Date(b.createdAt).getTime();
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue?.toLowerCase() || '';
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredData(filtered);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder(field === 'date' ? 'desc' : 'asc');
    }
  };

  const loadReport = async () => {
    try {
      setLoading(true);
      setError('');
      setLoadingProgress(0);
      setLoadingStatus('Fetching requests...');

      const requestParams: Record<string, unknown> = {
        populate: {
          insuranceClient: { populate: '*' },
          invoice: { populate: '*' },
          serviceProvider: { populate: '*' },
        },
        'filters[insuranceClient][id][$notNull]': true,
        'filters[status][$eq]': 'completed',
        sort: 'createdAt:desc',
        'pagination[limit]': -1,
      };
      if (!allTime) {
        if (periodStart) {
          requestParams['filters[createdAt][$gte]'] = `${periodStart}T00:00:00.000Z`;
        }
        if (periodEnd) {
          requestParams['filters[createdAt][$lte]'] = `${periodEnd}T23:59:59.999Z`;
        }
      }

      const requestsResponse = await requestService.getAll(requestParams);

      setLoadingProgress(15);
      const requests = requestsResponse.data || [];
      const totalRequests = requests.length;
      if (totalRequests === 0) {
        setReportData([]);
        setLoadingProgress(100);
        setLoadingStatus('Done');
        return;
      }

      const invoiceIds: number[] = [];
      const requestToInvoiceId = new Map<number, number>();
      requests.forEach((request: any, index: number) => {
        const attrs = request.attributes || request;
        const insuranceClient = attrs.insuranceClient?.data?.attributes ||
          attrs.insuranceClient?.data ||
          attrs.insuranceClient?.attributes ||
          attrs.insuranceClient;
        if (!insuranceClient) return;
        const invoiceId = attrs.invoice?.data?.id ||
          attrs.invoice?.data?.attributes?.id ||
          attrs.invoice?.id ||
          attrs.invoice;
        if (invoiceId) {
          invoiceIds.push(invoiceId);
          requestToInvoiceId.set(request.id, invoiceId);
        }
      });

      const uniqueInvoiceIds = Array.from(new Set(invoiceIds));
      const itemsByInvoiceId = new Map<number, any[]>();
      const progressStart = 15;
      const progressSpan = 70;

      for (let i = 0; i < uniqueInvoiceIds.length; i += BATCH_SIZE) {
        const batch = uniqueInvoiceIds.slice(i, i + BATCH_SIZE);
        const results = await Promise.all(
          batch.map((id) =>
            InvoiceService.getItems(id).catch(() => ({ data: { data: [] } }))
          )
        );
        batch.forEach((id, j) => {
          const allItems = results[j]?.data?.data || [];
          const mainItems = allItems.filter((item: any) => {
            const itemAttrs = item.attributes || item;
            const description = (itemAttrs.description || '').toLowerCase();
            return !description.includes('extra kilometer') &&
              !description.includes('extra km') &&
              !description.includes('extra kilometers');
          });
          itemsByInvoiceId.set(id, mainItems);
        });
        const pct = Math.min(
          progressStart + Math.round((progressSpan * (i + batch.length)) / uniqueInvoiceIds.length),
          85
        );
        setLoadingProgress(pct);
        setLoadingStatus(`Loading invoice items... ${Math.min(i + batch.length, uniqueInvoiceIds.length)} of ${uniqueInvoiceIds.length}`);
      }

      setLoadingProgress(90);
      setLoadingStatus('Building report...');

      const reportRows: ReportRow[] = [];
      for (const request of requests) {
        const attrs = request.attributes || request;
        const insuranceClient = attrs.insuranceClient?.data?.attributes ||
          attrs.insuranceClient?.data ||
          attrs.insuranceClient?.attributes ||
          attrs.insuranceClient;
        if (!insuranceClient) continue;

        const serviceProvider = attrs.serviceProvider?.data?.attributes ||
          attrs.serviceProvider?.data ||
          attrs.serviceProvider?.attributes ||
          attrs.serviceProvider;
        const serviceProviderId = attrs.serviceProvider?.data?.id ||
          attrs.serviceProvider?.id || null;
        const serviceProviderName = serviceProvider?.name || 'Not Assigned';

        const invoiceId = requestToInvoiceId.get(request.id);
        let mainInvoiceItem: any = null;
        if (invoiceId) {
          const mainItems = itemsByInvoiceId.get(invoiceId) || [];
          if (mainItems.length > 0) mainInvoiceItem = mainItems[0];
        }

        const locations = parseLocation(attrs.locationText);
        const itemAttrs = mainInvoiceItem?.attributes || mainInvoiceItem;

        reportRows.push({
          createdAt: attrs.createdAt || '',
          date: attrs.createdAt ? new Date(attrs.createdAt).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          }) : '',
          requestNumber: attrs.requestNumber || `R-${request.id}`,
          vehicleMake: insuranceClient.carMake || '',
          vehicleModel: insuranceClient.carModel || '',
          vehicleYear: insuranceClient.carYear || null,
          plateNumber: insuranceClient.plateNumber || '',
          phoneNumber: insuranceClient.phoneNumber || attrs.phoneNumber || '',
          locationFrom: locations.from,
          locationTo: locations.to,
          serviceType: attrs.serviceType || itemAttrs?.description || '',
          issueDescription: attrs.description || '',
          serviceProviderName: serviceProviderName,
          serviceProviderId: serviceProviderId,
        });
      }

      setReportData(reportRows);
      setLoadingProgress(100);
      setLoadingStatus('Done');
    } catch (err: any) {
      console.error('Error loading report:', err);
      setError('Failed to load report. Please try again.');
    } finally {
      setLoading(false);
      setLoadingProgress(0);
      setLoadingStatus('');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handlePrint = () => {
    window.print();
  };

  const getPeriodLabel = () =>
    allTime ? 'All time' : `${formatDate(periodStart)} - ${formatDate(periodEnd)}`;

  const getFilterSummaryLines = (): string[] => {
    const lines = [
      `Period: ${getPeriodLabel()}`,
      'Status: Completed services only',
      `Total Records: ${filteredData.length}`,
      `Sort: Newest to oldest`,
    ];
    if (selectedProvider !== 'all') {
      const provider = serviceProviders.find((p) => p.id.toString() === selectedProvider);
      lines.push(`Service Provider: ${provider?.attributes.name || selectedProvider}`);
    }
    if (selectedServiceType !== 'all') {
      lines.push(`Service Type: ${selectedServiceType}`);
    }
    if (searchQuery.trim()) {
      lines.push(`Search: ${searchQuery.trim()}`);
    }
    return lines;
  };

  const handleDownloadPDF = async () => {
    const doc = new jsPDF('landscape');

    const tableStartY = await addDarbReportHeader(doc, {
      title: 'Completed Services Report',
      subtitleLines: getFilterSummaryLines(),
    });

    // Prepare table data
    const tableData = filteredData.map((row) => [
      row.date,
      row.requestNumber,
      `${row.vehicleMake} ${row.vehicleModel}`.trim(),
      row.vehicleYear?.toString() || '',
      row.plateNumber,
      row.phoneNumber,
      row.locationFrom,
      row.locationTo,
      row.serviceProviderName,
      row.serviceType,
      row.issueDescription,
    ]);

    const headers = [
      'Date',
      'Request #',
      'Vehicle',
      'Year',
      'Plate',
      'Phone',
      'From',
      'To',
      'Service Provider',
      'Service Type',
      'Issue Description',
    ];

    autoTable(doc, {
      startY: tableStartY,
      head: [headers],
      body: tableData,
      theme: 'grid',
      headStyles: PDF_TABLE_HEAD_STYLES,
      styles: {
        fontSize: 7,
        cellPadding: 2,
      },
      columnStyles: {
        0: { cellWidth: 22 }, // Date
        1: { cellWidth: 25 }, // Request #
        2: { cellWidth: 30 }, // Vehicle
        3: { cellWidth: 12 }, // Year
        4: { cellWidth: 20 }, // Plate
        5: { cellWidth: 25 }, // Phone
        6: { cellWidth: 25 }, // From
        7: { cellWidth: 25 }, // To
        8: { cellWidth: 30 }, // Service Provider
        9: { cellWidth: 35 }, // Service Type
        10: { cellWidth: 40 }, // Issue Description
      },
    });

    const periodSlug = allTime ? 'All-Time' : `${periodStart}-to-${periodEnd}`;
    doc.save(`Completed-Services-Report-${periodSlug}.pdf`);
  };

  const handleDownloadExcel = async () => {
    const periodSlug = allTime ? 'All-Time' : `${periodStart}-to-${periodEnd}`;
    await downloadDarbExcelReport({
      sheetName: 'Completed Services',
      title: 'Completed Services Report',
      subtitleLines: getFilterSummaryLines(),
      columns: [
        { header: 'Date', key: 'date', width: 14 },
        { header: 'Request #', key: 'requestNumber', width: 16 },
        { header: 'Vehicle Make', key: 'vehicleMake', width: 16 },
        { header: 'Vehicle Model', key: 'vehicleModel', width: 16 },
        { header: 'Year', key: 'year', width: 8 },
        { header: 'Plate Number', key: 'plateNumber', width: 14 },
        { header: 'Phone', key: 'phoneNumber', width: 16 },
        { header: 'Location From', key: 'locationFrom', width: 22 },
        { header: 'Location To', key: 'locationTo', width: 22 },
        { header: 'Service Provider', key: 'serviceProviderName', width: 22 },
        { header: 'Service Type', key: 'serviceType', width: 22 },
        { header: 'Issue Description', key: 'issueDescription', width: 35 },
      ],
      rows: filteredData.map((row) => ({
        date: row.date,
        requestNumber: row.requestNumber,
        vehicleMake: row.vehicleMake,
        vehicleModel: row.vehicleModel,
        year: row.vehicleYear?.toString() || '',
        plateNumber: row.plateNumber,
        phoneNumber: row.phoneNumber,
        locationFrom: row.locationFrom,
        locationTo: row.locationTo,
        serviceProviderName: row.serviceProviderName,
        serviceType: row.serviceType,
        issueDescription: row.issueDescription,
      })),
      fileName: `Completed-Services-Report-${periodSlug}.xlsx`,
    });
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedProvider('all');
    setSelectedServiceType('all');
    setAllTime(true);
    setSortField('date');
    setSortOrder('desc');
  };

  return (
      <Box>
        <style>
          {`
            @media print {
              .no-print {
                display: none !important;
              }
              @page {
                margin: 1cm;
              }
            }
          `}
        </style>

        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} className="no-print">
          <Box>
            <Typography variant="h4">Completed Services Report</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              All completed services, sorted newest to oldest
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" startIcon={<ExcelIcon />} onClick={handleDownloadExcel} disabled={filteredData.length === 0 || loading}>
              Excel
            </Button>
            <Button variant="outlined" startIcon={<PdfIcon />} onClick={handleDownloadPDF} disabled={filteredData.length === 0 || loading}>
              PDF
            </Button>
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint} disabled={filteredData.length === 0}>
              Print
            </Button>
          </Stack>
        </Stack>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }} className="no-print">
          <Typography variant="h6" gutterBottom>
            Search & Filters
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField
              label="Search"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Request #, phone, plate, provider, service type..."
              sx={{ minWidth: 280 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Service Provider"
              size="small"
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="all">All Providers</MenuItem>
              {serviceProviders.map((provider) => (
                <MenuItem key={provider.id} value={provider.id.toString()}>
                  {provider.attributes.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Service Type"
              size="small"
              value={selectedServiceType}
              onChange={(e) => setSelectedServiceType(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="all">All Service Types</MenuItem>
              {serviceTypeOptions.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
            <Chip
              label={allTime ? 'All time' : 'Custom period'}
              color={allTime ? 'primary' : 'default'}
              onClick={() => setAllTime((prev) => !prev)}
              variant={allTime ? 'filled' : 'outlined'}
            />
            {!allTime && (
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Start Date"
                  type="date"
                  size="small"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  size="small"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            )}
            <Button variant="outlined" onClick={handleClearFilters}>
              Clear Filters
            </Button>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={loadReport}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Refresh Report'}
            </Button>
          </Stack>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ minHeight: 280 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              {loadingStatus}
              {loadingProgress > 0 && loadingProgress < 100 && (
                <Typography component="span" variant="body2" color="primary.main" fontWeight={600} sx={{ ml: 1 }}>
                  {loadingProgress}%
                </Typography>
              )}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={loadingProgress}
              sx={{ height: 10, borderRadius: 1, mb: 2 }}
            />
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress size={48} sx={{ opacity: 0.7 }} />
            </Box>
          </Box>
        ) : (
          <>
            {/* Summary Card */}
            <Card sx={{ mb: 3 }} className="no-print">
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Records
                </Typography>
                <Typography variant="h4">{filteredData.length}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {getPeriodLabel()}
                  {(selectedProvider !== 'all' || selectedServiceType !== 'all' || searchQuery.trim()) && ' (Filtered)'}
                </Typography>
              </CardContent>
            </Card>

            {/* Report Table */}
            <Paper>
              <Box sx={{ p: 3 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Box
                    component="img"
                    src="/cropped-pixelcut-export-removebg-2048x691.png"
                    alt="DARB National Service"
                    sx={{ width: 180, maxWidth: '100%', mb: 1 }}
                  />
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Completed Services Report
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {getPeriodLabel()} · Completed services only
                  </Typography>
                </Box>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === 'date'}
                            direction={sortField === 'date' ? sortOrder : 'asc'}
                            onClick={() => handleSort('date')}
                          >
                            Date
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === 'requestNumber'}
                            direction={sortField === 'requestNumber' ? sortOrder : 'asc'}
                            onClick={() => handleSort('requestNumber')}
                          >
                            Request #
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === 'vehicleMake'}
                            direction={sortField === 'vehicleMake' ? sortOrder : 'asc'}
                            onClick={() => handleSort('vehicleMake')}
                          >
                            Vehicle
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === 'vehicleYear'}
                            direction={sortField === 'vehicleYear' ? sortOrder : 'asc'}
                            onClick={() => handleSort('vehicleYear')}
                          >
                            Year
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === 'plateNumber'}
                            direction={sortField === 'plateNumber' ? sortOrder : 'asc'}
                            onClick={() => handleSort('plateNumber')}
                          >
                            Plate
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === 'phoneNumber'}
                            direction={sortField === 'phoneNumber' ? sortOrder : 'asc'}
                            onClick={() => handleSort('phoneNumber')}
                          >
                            Phone
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === 'locationFrom'}
                            direction={sortField === 'locationFrom' ? sortOrder : 'asc'}
                            onClick={() => handleSort('locationFrom')}
                          >
                            From
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === 'locationTo'}
                            direction={sortField === 'locationTo' ? sortOrder : 'asc'}
                            onClick={() => handleSort('locationTo')}
                          >
                            To
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === 'serviceProviderName'}
                            direction={sortField === 'serviceProviderName' ? sortOrder : 'asc'}
                            onClick={() => handleSort('serviceProviderName')}
                          >
                            Service Provider
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === 'serviceType'}
                            direction={sortField === 'serviceType' ? sortOrder : 'asc'}
                            onClick={() => handleSort('serviceType')}
                          >
                            Service Type
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>
                          <TableSortLabel
                            active={sortField === 'issueDescription'}
                            direction={sortField === 'issueDescription' ? sortOrder : 'asc'}
                            onClick={() => handleSort('issueDescription')}
                          >
                            Issue Description
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                            <Typography color="textSecondary">
                              {searchQuery.trim() || selectedProvider !== 'all' || selectedServiceType !== 'all'
                                ? 'No completed services match the current filters.'
                                : 'No completed services found.'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredData.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell>{row.date}</TableCell>
                            <TableCell>{row.requestNumber}</TableCell>
                            <TableCell>
                              {row.vehicleMake} {row.vehicleModel}
                            </TableCell>
                            <TableCell>{row.vehicleYear || '-'}</TableCell>
                            <TableCell>{row.plateNumber || '-'}</TableCell>
                            <TableCell>{row.phoneNumber || '-'}</TableCell>
                            <TableCell>{row.locationFrom || '-'}</TableCell>
                            <TableCell>{row.locationTo || '-'}</TableCell>
                            <TableCell>{row.serviceProviderName || '-'}</TableCell>
                            <TableCell>{row.serviceType || '-'}</TableCell>
                            <TableCell>{row.issueDescription || '-'}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </>
        )}
      </Box>
  );
};

export default RequestItemsReport;

